/** Which of a palette's two inks reads on a runtime-coloured surface. */
export type AccentInk = 'dark' | 'light';

/** `#RRGGBB` → the three channels, 0–255. Assumes the schema already validated the shape. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const value = Number.parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 0xff, g: (value >> 8) & 0xff, b: value & 0xff };
}

/** WCAG 2.x relative luminance of one sRGB channel (0–255). */
function linear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG 2.x relative luminance, 0 (black) – 1 (white). */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/**
 * The ink that contrasts more with a brand accent.
 *
 * Shared by both clients so a `#1D4ED8` accent gets the same light ink in the
 * staff portal and on the phone. Compares the WCAG contrast ratio against white
 * and against black — the two ends every palette's inks sit near — and picks the
 * end with the higher ratio, which is exactly the ratio a reader experiences.
 */
export function accentInkFor(hex: string): AccentInk {
  const l = relativeLuminance(hex);
  const againstWhite = (1 + 0.05) / (l + 0.05);
  const againstBlack = (l + 0.05) / (0 + 0.05);
  return againstWhite >= againstBlack ? 'light' : 'dark';
}
